import React from 'react';
import { FileText, Archive, Clock, CheckCircle, Download, RefreshCw } from 'lucide-react';
import { Badge, StatCard, Th, Td } from '../WMSShared';
import axios from 'axios';

export default function AuditTab({
  API, auditData, auditTab, switchAuditTab, loadAudit, setReconModal, submitReconciliation, user,
  activity, loadActivity
}) {
  const AUDIT_TABS = [
    ['overview',        'Overview'],
    ['audit-log',       'Audit Trail'],
    ['stock-ledger',    'Stock Ledger'],
    ['reconciliation',  'Reconciliation'],
    ['valuation',       'Inventory Valuation'],
    ['approvals',       'Approvals'],
    ['activity',        'Activity Log'],
  ];

  const approveOrReject = async (a, decision) => {
    const endpoint = a.type === 'reconciliation_adjustment'
      ? `${API}/audit/reconciliation/approve/${a.approval_id}`
      : a.type === 'loss_report'
      ? `${API}/audit/loss/approve/${a.approval_id}`
      : `${API}/audit/approvals/${a.approval_id}`;
    await axios.put(endpoint, { decision, approved_by: user.email, notes: decision === 'approved' ? 'Approved' : 'Rejected' });
    loadAudit();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="text-xl font-bold">Audit & Compliance</h2><p className="text-sm text-gray-500">Immutable records for auditors</p></div>
        <div className="flex gap-2">
          <button onClick={loadAudit} className="flex items-center gap-2 text-sm text-gray-600 border px-3 py-2 rounded-lg hover:bg-gray-50"><RefreshCw className="w-4 h-4" />Load Data</button>
          <button onClick={() => window.open(`${API}/audit/export/audit-log`, '_blank')} className="flex items-center gap-2 text-sm text-gray-600 border px-3 py-2 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" />Audit Log CSV</button>
          <button onClick={() => window.open(`${API}/audit/export/stock-ledger`, '_blank')} className="flex items-center gap-2 text-sm text-gray-600 border px-3 py-2 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" />Stock Ledger CSV</button>
        </div>
      </div>

      <div className="flex gap-2 mb-6 border-b pb-3">
        {AUDIT_TABS.map(([id, label]) => (
          <button key={id} onClick={() => switchAuditTab(id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${auditTab === id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {label}
          </button>
        ))}
      </div>

      {auditTab === 'overview' && (
        <div>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <StatCard label="Audit Entries" value={auditData.stats.total_audit_entries || 0} icon={FileText} color="blue" sub="Immutable records" />
            <StatCard label="Stock Movements" value={auditData.stats.total_stock_movements || 0} icon={Archive} color="emerald" sub="Ledger entries" />
            <StatCard label="Pending Approvals" value={auditData.stats.pending_approvals || 0} icon={Clock} color="orange" sub="Awaiting review" />
            <StatCard label="Physical Counts" value={auditData.stats.total_physical_counts || 0} icon={CheckCircle} color="purple" sub="Variance checks" />
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <h3 className="font-semibold text-blue-800 mb-2">Audit System Features</h3>
            <div className="grid grid-cols-2 gap-3 text-sm text-blue-700">
              {['Immutable audit trail (write-only)', 'Full stock movement ledger', 'Financial reconciliation', 'FIFO/LIFO/Average valuation',
                'Cost audit trail per product', 'Physical count variance reports', 'Approval workflow for adjustments', 'CSV export for external auditors']
                .map(f => <div key={f} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-blue-500" />{f}</div>)}
            </div>
          </div>
        </div>
      )}

      {auditTab === 'audit-log' && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr><Th>Audit ID</Th><Th>Timestamp</Th><Th>Action</Th><Th>Entity</Th><Th>User</Th><Th>Details</Th></tr></thead>
              <tbody className="divide-y">
                {auditData.log.map((e, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <Td className="font-mono text-[10px]">{e.audit_id?.slice(0, 16)}</Td>
                    <Td className="text-xs text-gray-500">{e.timestamp?.split('.')[0]?.replace('T', ' ')}</Td>
                    <Td><Badge status={e.action} /></Td>
                    <Td className="text-xs">{e.entity_type} <span className="text-gray-400">{e.entity_id?.slice(0, 15)}</span></Td>
                    <Td className="text-xs">{e.user}</Td>
                    <Td className="text-xs text-gray-500 max-w-[200px] truncate">{e.details}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {auditData.log.length === 0 && <div className="p-8 text-center text-gray-400">Click "Load Data" to fetch audit trail</div>}
        </div>
      )}

      {auditTab === 'stock-ledger' && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr><Th>ID</Th><Th>Timestamp</Th><Th>Product</Th><Th>Type</Th><Th>Qty</Th><Th>Cost</Th><Th>Value</Th><Th>Balance</Th><Th>Reference</Th></tr></thead>
              <tbody className="divide-y">
                {auditData.ledger.map((e, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <Td className="font-mono text-[10px]">{e.ledger_id}</Td>
                    <Td className="text-xs text-gray-500">{e.timestamp?.split('.')[0]?.replace('T', ' ')}</Td>
                    <Td className="font-medium text-xs">{e.product_name || e.product_id}</Td>
                    <Td><span className={`text-xs px-2 py-0.5 rounded-full ${e.movement_type === 'received' ? 'bg-emerald-100 text-emerald-700' : e.movement_type === 'sold' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>{e.movement_type}</span></Td>
                    <Td className={e.quantity > 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>{e.quantity > 0 ? '+' : ''}{e.quantity}</Td>
                    <Td className="text-gray-500">S${e.unit_cost}</Td>
                    <Td className="font-medium">S${e.total_value}</Td>
                    <Td className="font-semibold">{e.balance_after}</Td>
                    <Td className="text-[10px] text-gray-400">{e.reference_type}: {e.reference_id?.slice(0, 12)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {auditData.ledger.length === 0 && <div className="p-8 text-center text-gray-400">Click "Load Data" to fetch stock movements</div>}
        </div>
      )}

      {auditTab === 'reconciliation' && auditData.reconciliation && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <h4 className="text-sm text-gray-500 mb-2">Purchases (Cost)</h4>
              <p className="text-2xl font-bold text-blue-600">S${auditData.reconciliation.purchases?.total_cost?.toLocaleString()}</p>
              <p className="text-xs text-gray-400">{auditData.reconciliation.purchases?.order_count} purchase orders</p>
            </div>
            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <h4 className="text-sm text-gray-500 mb-2">Sales (Revenue)</h4>
              <p className="text-2xl font-bold text-emerald-600">S${auditData.reconciliation.sales?.revenue?.toLocaleString()}</p>
              <p className="text-xs text-gray-400">COGS: S${auditData.reconciliation.sales?.cogs?.toLocaleString()} | Margin: {auditData.reconciliation.margin}%</p>
            </div>
            <div className="bg-white rounded-xl p-5 border shadow-sm">
              <h4 className="text-sm text-gray-500 mb-2">Current Inventory</h4>
              <p className="text-2xl font-bold text-purple-600">S${auditData.reconciliation.inventory?.current_value?.toLocaleString()}</p>
              <p className="text-xs text-gray-400">{auditData.reconciliation.inventory?.total_units} units</p>
            </div>
          </div>
          <div className={`rounded-xl p-5 border-2 ${auditData.reconciliation.reconciliation?.status === 'balanced' ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">{auditData.reconciliation.reconciliation?.status === 'balanced' ? '✓ Reconciliation Balanced' : '⚠ Reconciliation Variance Detected'}</h4>
              {auditData.reconciliation.reconciliation?.status !== 'balanced' && (
                <button onClick={() => setReconModal(true)} className="flex items-center gap-2 bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 shadow-sm">
                  <FileText className="w-4 h-4" />Request Reconciliation
                </button>
              )}
            </div>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div><p className="text-gray-500">Purchased Value</p><p className="font-medium">S${auditData.reconciliation.reconciliation?.purchased_value?.toLocaleString()}</p></div>
              <div><p className="text-gray-500">Sold (COGS)</p><p className="font-medium">S${auditData.reconciliation.reconciliation?.sold_cogs?.toLocaleString()}</p></div>
              <div><p className="text-gray-500">Expected Inventory</p><p className="font-medium">S${auditData.reconciliation.reconciliation?.expected_inventory_value?.toLocaleString()}</p></div>
              <div><p className="text-gray-500">Variance</p><p className={`font-bold ${auditData.reconciliation.reconciliation?.variance === 0 ? 'text-emerald-600' : 'text-red-600'}`}>S${auditData.reconciliation.reconciliation?.variance}</p></div>
            </div>
          </div>
        </div>
      )}

      {auditTab === 'valuation' && auditData.valuation && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">Method: <span className="font-semibold uppercase">{auditData.valuation.method}</span></p>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Value</p>
              <p className="text-2xl font-bold text-blue-600">S${auditData.valuation.total_value?.toLocaleString()}</p>
              <p className="text-xs text-gray-400">{auditData.valuation.total_units} units across {auditData.valuation.total_products} products</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr><Th>SKU</Th><Th>Product</Th><Th>Stock</Th><Th>Unit Cost</Th><Th>Total Value</Th><Th>Warehouse</Th></tr></thead>
              <tbody className="divide-y">
                {(auditData.valuation.valuations || []).map((v, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <Td className="font-mono text-xs">{v.sku}</Td><Td className="font-medium">{v.name}</Td><Td>{v.stock}</Td>
                    <Td>S${v.unit_cost?.toFixed(2)}</Td><Td className="font-semibold text-blue-600">S${v.total_value?.toLocaleString()}</Td>
                    <Td className="text-xs text-gray-500">{v.warehouse_id}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {auditTab === 'approvals' && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr><Th>ID</Th><Th>Type</Th><Th>Details</Th><Th>Amount</Th><Th>Reason</Th><Th>Requested By</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
              <tbody className="divide-y">
                {auditData.approvals.map((a, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <Td className="font-mono text-[10px]">{a.approval_id}</Td>
                    <Td><span className={`text-xs px-2 py-0.5 rounded-full ${a.type === 'reconciliation_adjustment' ? 'bg-amber-100 text-amber-700' : a.type === 'loss_report' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                      {a.type === 'reconciliation_adjustment' ? 'Reconciliation' : a.type === 'loss_report' ? 'Loss Report' : 'Stock Adjust'}
                    </span></Td>
                    <Td className="font-medium text-xs">{a.type === 'reconciliation_adjustment' ? `Variance: S$${a.adjustment_value}` : `${a.product_name}: ${a.adjustment_qty} units`}</Td>
                    <Td className="font-semibold">{a.type === 'reconciliation_adjustment' ? `S$${a.adjustment_value}` : a.type === 'loss_report' ? `S$${a.total_loss_value || 0}` : `${a.adjustment_qty} units`}</Td>
                    <Td className="text-xs text-gray-500 max-w-[150px] truncate">{a.reason || a.loss_category || a.category || '-'}</Td>
                    <Td className="text-xs">{a.requested_by}</Td>
                    <Td><Badge status={a.status} /></Td>
                    <Td>
                      {a.status === 'pending' && (
                        <div className="flex gap-1">
                          <button onClick={() => approveOrReject(a, 'approved')} className="text-xs bg-emerald-500 text-white px-2 py-1 rounded hover:bg-emerald-600">Approve</button>
                          <button onClick={() => approveOrReject(a, 'rejected')} className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600">Reject</button>
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {auditData.approvals.length === 0 && <div className="p-8 text-center text-gray-400">No approval requests</div>}
        </div>
      )}

      {auditTab === 'activity' && (
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
            <p className="text-xs text-gray-500">All user actions logged in real-time</p>
            {loadActivity && <button onClick={loadActivity} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"><RefreshCw className="w-3 h-3" />Refresh</button>}
          </div>
          <div className="divide-y">
            {(activity || []).map((a, i) => (
              <div key={i} className="flex items-start gap-4 px-5 py-3 hover:bg-gray-50">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                  a.action === 'create' ? 'bg-emerald-100' : a.action === 'delete' ? 'bg-red-100' : a.action === 'login' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <span className="text-xs">{a.action === 'create' ? '+' : a.action === 'delete' ? '×' : a.action === 'login' ? '→' : '~'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm"><span className="font-medium capitalize">{a.action}</span> <span className="text-gray-500">{a.entity_type}</span></p>
                  {a.details && <p className="text-xs text-gray-500 mt-0.5">{a.details}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[10px] text-gray-400">{a.timestamp?.split('T')[0]}</p>
                  <p className="text-[10px] text-gray-400">{a.timestamp?.split('T')[1]?.split('.')[0]}</p>
                </div>
              </div>
            ))}
            {(!activity || activity.length === 0) && <div className="p-8 text-center text-gray-400">No activity yet</div>}
          </div>
        </div>
      )}
    </div>
  );
}
